import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import {
  apiGetRelationshipLevels,
  apiUpdateRelationshipLevel,
  apiUpdateTop30,
} from "@api/relationshipLevel";
import type { RelationshipLevelRes } from "@api/relationshipLevel";

export function useRelationshipLevels() {
  return useQuery<RelationshipLevelRes[]>({
    queryKey: ["relationshipLevels"],
    queryFn: apiGetRelationshipLevels,
  });
}

export function useUpdateRelationshipLevel() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({
      profileId,
      level,
    }: {
      profileId: string;
      level: number | null;
    }) => apiUpdateRelationshipLevel(profileId, level),
    onMutate: async ({ profileId, level }) => {
      await queryClient.cancelQueries({ queryKey: ["profileList"] });
      await queryClient.cancelQueries({ queryKey: ["repTop30"] });
      await queryClient.cancelQueries({ queryKey: ["profile", profileId] });

      const previousProfileList = queryClient.getQueriesData({ queryKey: ["profileList"] });
      const previousRepTop30 = queryClient.getQueriesData({ queryKey: ["repTop30"] });
      const previousProfile = queryClient.getQueryData(["profile", profileId]);

      const updateProfile = (profile: any) =>
        profile.id === profileId ? { ...profile, relationship_level: level } : profile;

      // Optimistically update all profileList queries (infinite query pages)
      queryClient.setQueriesData({ queryKey: ["profileList"] }, (old: any) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: page.data.map(updateProfile),
          })),
        };
      });

      // Optimistically update all repTop30 queries
      queryClient.setQueriesData({ queryKey: ["repTop30"] }, (old: any) => {
        if (!old?.data) return old;
        return { ...old, data: old.data.map(updateProfile) };
      });

      // Optimistically update profile detail
      queryClient.setQueryData(["profile", profileId], (old: any) => {
        if (!old) return old;
        return { ...old, relationship_level: level };
      });

      return { previousProfileList, previousRepTop30, previousProfile, profileId };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousProfileList) {
        for (const [key, data] of context.previousProfileList) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context?.previousRepTop30) {
        for (const [key, data] of context.previousRepTop30) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context?.previousProfile)
        queryClient.setQueryData(["profile", context.profileId], context.previousProfile);
      enqueueSnackbar("Failed to update relationship level", {
        variant: "error",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["profileList"] });
      queryClient.invalidateQueries({ queryKey: ["repTop30"] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });
}

export function useUpdateTop30() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({
      profileId,
      top30,
    }: {
      profileId: string;
      top30: boolean;
    }) => apiUpdateTop30(profileId, top30),
    onMutate: async ({ profileId, top30 }) => {
      await queryClient.cancelQueries({ queryKey: ["profileList"] });
      await queryClient.cancelQueries({ queryKey: ["repTop30"] });
      await queryClient.cancelQueries({ queryKey: ["profile", profileId] });

      const previousProfileList = queryClient.getQueriesData({ queryKey: ["profileList"] });
      const previousRepTop30 = queryClient.getQueriesData({ queryKey: ["repTop30"] });
      const previousProfile = queryClient.getQueryData(["profile", profileId]);

      const updateProfile = (profile: any) =>
        profile.id === profileId ? { ...profile, top30 } : profile;

      // Optimistically update all profileList queries (infinite query pages)
      queryClient.setQueriesData({ queryKey: ["profileList"] }, (old: any) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: page.data.map(updateProfile),
          })),
        };
      });

      // Optimistically update all repTop30 queries. A contact that is no longer
      // Top 30 must drop out of the view entirely.
      queryClient.setQueriesData({ queryKey: ["repTop30"] }, (old: any) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: top30
            ? old.data.map(updateProfile)
            : old.data.filter((p: any) => p.id !== profileId),
        };
      });

      // Optimistically update profile detail
      queryClient.setQueryData(["profile", profileId], (old: any) => {
        if (!old) return old;
        return { ...old, top30 };
      });

      return { previousProfileList, previousRepTop30, previousProfile, profileId };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousProfileList) {
        for (const [key, data] of context.previousProfileList) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context?.previousRepTop30) {
        for (const [key, data] of context.previousRepTop30) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context?.previousProfile)
        queryClient.setQueryData(["profile", context.profileId], context.previousProfile);
      enqueueSnackbar("Failed to update Top 30", {
        variant: "error",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["profileList"] });
      queryClient.invalidateQueries({ queryKey: ["repTop30"] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });
}
